/**
 * ComponentFactory.js
 * ----------------------------------------------------------------------------
 * Factory for constructing modular calendar components using dependency injection.
 *
 * Refactor goals:
 * - Remove noisy console logging in createRendererAdapter()
 * - Use logger consistently (no console.* in production paths)
 * - Keep validation minimal and actionable
 * - Standardize error reporting through errorHandler
 */

import EventDataAdapter from '../adapters/EventDataAdapter.js';
import ModalAdapter from '../adapters/ModalAdapter.js';
import RendererAdapter from '../adapters/RenderAdapter.js'; // existing RenderAdapter

export default class ComponentFactory {
  constructor(container) {
    this.container = container;
    this.logger = container.logger;
    this.config = container.config;
    this.errorHandler = container.errorHandler;
    this.initialized = false;
  }

  /**
   * Initialize the factory (idempotent)
   */
  initialize() {
    if (this.initialized) return;

    this.logger.debug('🏭 Initializing component factory...');
    this.initialized = true;
    this.logger.debug('✅ Component factory initialized');
  }

  _assertInitialized() {
    if (!this.initialized) {
      // This is a dev-time contract error; treat as fatal for clarity.
      throw new Error('ComponentFactory is not initialized. Call factory.initialize() first.');
    }
  }

  /**
   * Create Event Data Adapter
   */
  async createEventDataAdapter() {
    this._assertInitialized();
    this.logger.debug('Creating EventDataAdapter...');

    try {
      const calendarEl = document.querySelector(this.config.selectors.calendar);
      if (!calendarEl) {
        throw new Error(`Calendar element not found: ${this.config.selectors.calendar}`);
      }

      const adapter = new EventDataAdapter(calendarEl, this.logger);

      const parseSuccess = adapter.initialize();
      if (!parseSuccess) {
        throw new Error('Failed to parse events from DOM');
      }

      this.logger.debug('✅ EventDataAdapter created successfully');
      return adapter;
    } catch (error) {
      this.errorHandler.handleError('Failed to create EventDataAdapter', error, {
        category: 'component_creation',
        component: 'EventDataAdapter',
      });
      throw error;
    }
  }

  /**
   * Create Modal Adapter (optional)
   */
  async createModalAdapter() {
    this._assertInitialized();
    this.logger.debug('Creating ModalAdapter...');

    try {
      const adapter = new ModalAdapter(this.logger);
      const initSuccess = adapter.initialize();

      if (!initSuccess) {
        this.logger.warn('⚠️ Modal functionality not available');
        return null;
      }

      this.logger.debug('✅ ModalAdapter created successfully');
      return adapter;
    } catch (error) {
      // Modal is non-critical; continue without it.
      this.logger.warn('⚠️ ModalAdapter creation failed, continuing without modal:', error.message);
      return null;
    }
  }

  /**
   * Create Renderer Adapter (using existing RenderAdapter)
   */
  async createRendererAdapter(eventDataAdapter) {
    this._assertInitialized();
    this.logger.debug('Creating RendererAdapter...');

    try {
      if (!eventDataAdapter) {
        throw new Error('EventDataAdapter is required for RendererAdapter');
      }
      if (typeof eventDataAdapter.getEventManager !== 'function') {
        throw new Error('EventDataAdapter must implement getEventManager()');
      }

      const eventManager = eventDataAdapter.getEventManager();
      if (!eventManager) {
        throw new Error('EventDataAdapter.getEventManager() returned null/undefined');
      }

      // Construct adapter with injected dependencies
      const adapter = new RendererAdapter(
        this.config,
        this.logger,
        this.errorHandler,
        eventManager,
        {}
      );

      const initSuccess = await adapter.initialize();
      if (!initSuccess) {
        throw new Error('RendererAdapter initialization failed');
      }

      this.logger.debug('✅ RendererAdapter created successfully');
      return adapter;
    } catch (error) {
      this.errorHandler.handleError('Failed to create RendererAdapter', error, {
        category: 'component_creation',
        component: 'RendererAdapter',
        action: 'renderer-creation-failed',
      });
      throw error;
    }
  }

  /**
   * Create Event Click Handler (plain object adapter)
   */
  async createEventClickHandler(eventDataAdapter, modalAdapter) {
    this._assertInitialized();
    this.logger.info('Creating EventClickHandler...');

    try {
      const handler = {
        handleEventClick: (titleText, dateText) => {
          try {
            this.logger.info('Handling event click:', titleText);

            const event = eventDataAdapter.findEventByTitle(titleText);
            if (!event) {
              this.logger.warn('Event not found:', titleText);
              return false;
            }

            if (modalAdapter && modalAdapter.isAvailable()) {
              return modalAdapter.showEventModal(event, dateText);
            }

            this.logger.info('Event clicked (no modal available):', event.title);
            return true;
          } catch (error) {
            this.errorHandler.handleError('Error handling event click', error, {
              category: 'event_handling',
            });
            return false;
          }
        },
      };

      this.logger.info('✅ EventClickHandler created successfully');
      return handler;
    } catch (error) {
      this.errorHandler.handleError('Failed to create EventClickHandler', error, {
        category: 'component_creation',
        component: 'EventClickHandler',
      });
      throw error;
    }
  }

  /**
   * Get factory statistics
   */
  getStats() {
    return {
      initialized: this.initialized,
      hasContainer: !!this.container,
      hasLogger: !!this.logger,
      hasConfig: !!this.config,
      hasErrorHandler: !!this.errorHandler,
    };
  }

  /**
   * Clean up factory
   */
  async destroy() {
    this.logger.info('🗑️ Destroying ComponentFactory...');
    this.initialized = false;
    this.logger.info('✅ ComponentFactory destroyed');
  }
}
